import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import { handleOf } from './handle';
import HandleAvatar from './HandleAvatar';
import PendingHandleAvatar from './PendingHandleAvatar';
import { identityForEdit, identityForReply, NEW_DISCUSSION, replaceComposerAvatar } from './composerIdentity';

/**
 * Task 4.1 -- the composer says who you will post as.
 *
 * Three composers, three answers (§9, Phase 4):
 *
 *   - a REPLY shows your handle in this Issue, or says you will get one;
 *   - a NEW DISCUSSION always says you will get one, because it is a new Issue;
 *   - an EDIT shows the handle the post already carries.
 *
 * Each does two things: swaps the avatar core draws from the `user` attr for
 * one drawn from the handle, and adds a line to the header saying the same in
 * words. On a phone the avatar is hidden by core's own CSS, so the line is what
 * carries the message there.
 *
 * THE COMPOSERS ARE LAZY-LOADED CHUNKS, which is why every hook below names a
 * module path rather than importing the class. Core's `app.composer.load()`
 * fetches a composer's code only when one first opens, and the string form of
 * `extend`/`override` (`flarum/common/extend.ts:37-41`) waits for that. Importing the class here
 * would pull it into the main bundle and bypass the lazy split.
 *
 * NEVER WRITE A DYNAMIC-IMPORT CALL IN A COMMENT IN THIS PROJECT. Flarum's
 * `autoChunkNameLoader` rewrites the word import followed by an opening
 * parenthesis and a quote wherever it appears, comments included, and
 * injects a block comment whose closing marker ends yours early -- the build
 * then fails on a "parse error" in prose. Found at 4.1.
 *
 * Each subclass is patched separately, not `ComposerBody`, so a composer this
 * task knows nothing about -- flarum/messages ships one, installed and not
 * enabled -- keeps core's behaviour instead of being given an Issue it does not
 * have.
 */
export default function composer() {
  register('flarum/forum/components/ReplyComposer', function () {
    return identityForReply(this.attrs.discussion);
  });
  register('flarum/forum/components/DiscussionComposer', function () {
    return NEW_DISCUSSION;
  });
  register('flarum/forum/components/EditPostComposer', function () {
    return identityForEdit(handleOf(this.attrs.post));
  }, 'edited_as');
}
function register(path, identityOf, existingKey) {
  if (existingKey === void 0) {
    existingKey = 'posting_as';
  }
  /*
   * `override` on a subclass whose `view` is inherited defines it on that
   * subclass's prototype only, so ComposerBody itself is untouched.
   */
  override(path, 'view', function (original) {
    const tree = original();
    const identity = identityOf.call(this);
    if (identity !== null) {
      replaceComposerAvatar(tree, avatarFor(identity));
    }
    return tree;
  });
  extend(path, 'headerItems', function (items) {
    const identity = identityOf.call(this);
    if (identity === null) return;

    // Negative priority: after the reply title and the discussion title input,
    // both of which core adds at 0 or above.
    items.add('ideasynq-identity', m("h3", {
      className: "IdeaSynqComposerIdentity"
    }, sentenceFor(identity, existingKey)), -10);
  });
}
function avatarFor(identity) {
  if (identity.kind === 'existing') {
    // No `self` ring. The ring picks you out among other people's posts, and a
    // composer has no other people in it -- except a moderator's edit, where
    // the handle is the author's and a ring would be wrong.
    return HandleAvatar(identity.handle, {
      className: 'ComposerBody-avatar'
    });
  }
  return PendingHandleAvatar('ComposerBody-avatar');
}
function sentenceFor(identity, existingKey) {
  if (identity.kind === 'existing') {
    return app.translator.trans(`ideasynq-handles.forum.composer.${existingKey}`, {
      handle: m("span", {
        className: "IdeaSynqHandle"
      }, identity.handle)
    });
  }
  return app.translator.trans('ideasynq-handles.forum.composer.new_handle');
}